import { FormControl, FormField } from '@/components/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Form, FormItem, FormLabel } from '@/components/wm';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { OPPORTUNITY_MEASURES } from '../utils';
import { Widget } from './Widget';

// Radix Select cannot hold an empty value, so "all" stands for an empty one.
const ALL = 'all';

const fromSelect = value => (value === ALL ? '' : value);

export function NewOpportunitiesData({ data, onChangeField, onCompleted }) {
    const
        siteId = data.data?.siteId || '',
        utilityKindId = data.data?.utilityKindId || '',
        sitesOptions = WiseMetering.zones.activeSites().map(model => ({ id: model.id, name: model.get('name') })),
        utilityOptions = WiseMetering.utilityKinds
            .filter(kind => WiseMetering.opportunities.some(opportunity => opportunity.get('utility_kind_id') === kind.id))
            .map(kind => ({ id: kind.id, name: i18next.t(`utility_kinds.${kind.get('slug')}`).capitalize() }));

    const form = useForm({
        defaultValues: {
            measure: data.data?.measure || 'cost',
            siteId: siteId || ALL,
            utilityKindId: utilityKindId || ALL
        }
    });

    useEffect(() => {
        onCompleted(true);

        if (!data.title) {
            onChangeField('title')(i18next.t('glossary.opportunities').capitalize());
        }

        if (!data.subtitle) {
            onChangeField('subtitle')(WiseMetering.currentOrganization.get('name'));
        }
    }, []);

    const setData = changes => onChangeField('data')({ ...data.data, ...changes });

    const select = ({ label, name, onChange, options }) => (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                        <Select
                            onValueChange={val => {
                                field.onChange(val);
                                onChange(val);
                            }}
                            value={field.value}
                        >
                            <SelectTrigger className="tw-ml-0">
                                <SelectValue/>
                            </SelectTrigger>
                            <SelectContent>
                                {options.map(option => (
                                    <SelectItem disabled={option.disabled} key={option.id} value={option.id}>
                                        {option.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FormControl>
                </FormItem>
            )}
        />
    );

    return (
        <div className="tw-flex tw-gap-6">
            <div className="tw-flex-1">
                <Form {...form}>
                    {select({
                        label: i18next.t('glossary.site').capitalize(),
                        name: 'siteId',
                        onChange: val => {
                            setData({ siteId: fromSelect(val) });

                            const site = WiseMetering.zones.get(fromSelect(val));

                            onChangeField('subtitle')(site ? site.get('name') : WiseMetering.currentOrganization.get('name'));
                        },
                        options: [{ id: ALL, name: i18next.t('dashboard.custom_dashboard.all_sites') }, ...sitesOptions]
                    })}
                    {select({
                        label: i18next.t('glossary.utility').capitalize(),
                        name: 'utilityKindId',
                        onChange: val => {
                            if (val === ALL && form.getValues('measure') === 'consumption') {
                                form.setValue('measure', 'cost');
                                setData({ measure: 'cost', utilityKindId: '' });
                            } else if (val === ALL) {
                                setData({ utilityKindId: '' });
                            } else {
                                setData({ utilityKindId: val });
                            }
                        },
                        options: [{ id: ALL, name: i18next.t('dashboard.custom_dashboard.all_utilities') }, ...utilityOptions]
                    })}
                    {select({
                        label: i18next.t('dashboard.custom_dashboard.opportunities_measure'),
                        name: 'measure',
                        onChange: val => setData({ measure: val }),
                        options: OPPORTUNITY_MEASURES.map(measure => ({
                            disabled: measure === 'consumption' && !utilityKindId,
                            id: measure,
                            name: i18next.t(`dashboard.custom_dashboard.opportunities_measures.${measure}`)
                        }))
                    })}
                </Form>
            </div>
            <div className="tw-w-[320px] tw-h-[200px] tw-flex-shrink-0">
                <Widget widget={data}/>
            </div>
        </div>
    );
}
